import axios from 'axios';
import getApiInstance from '../common/apiInstance';
import { getApiErrorMessage } from '../common/apiErrorMessage';
import type { BillingInterval } from './subscriptionPlans';

const subscriptionApi = getApiInstance('/subscription', 'no-settings');

const SUBSCRIPTION_READ_CONFIG = {
  skipGlobalErrorToast: true,
  skipAuthRedirect: true,
  validateStatus: (status: number) => status === 200 || status === 401,
} as const;

const SUBSCRIPTION_WRITE_CONFIG = {
  skipAuthRedirect: true,
} as const;

const SESSION_EXPIRED_MESSAGE =
  'Your session expired. Sign in again to continue.';

export type SubscriptionStatus =
  | 'none'
  | 'active'
  | 'past_due'
  | 'canceled'
  | 'trialing';

export type FreeTierLimits = {
  puzzlesPerDay: number | null;
  srsReviewsPerDay: number | null;
  replayNewGameCooldownDays: number;
  replayHistoryLimit: number;
  gameAnalysesPerDay: number;
};

export type FreeTierUsage = {
  puzzlesToday: number;
  srsReviewsToday: number;
  gameAnalysesToday: number;
  replayNewGameAvailableAt?: string;
};

export type AnalysisUsage = {
  limit: number;
  usedToday: number;
};

export type AnalysisBuffer = {
  target: number;
  readyCount: number;
  pendingCount: number;
};

export type SubscriptionSummary = {
  status: SubscriptionStatus;
  planId?: 'pro';
  billingInterval?: BillingInterval;
  currentPeriodEnd?: string;
  isPro: boolean;
  hasFullAccess: boolean;
  isLifetimeMember: boolean;
  isTrial: boolean;
  trialEndsAt?: string;
  freeTier?: {
    limits: FreeTierLimits;
    usage: FreeTierUsage;
  };
  analysisUsage?: AnalysisUsage;
  analysisBuffer?: AnalysisBuffer;
};

export const DEFAULT_SUBSCRIPTION: SubscriptionSummary = {
  status: 'none',
  isPro: false,
  hasFullAccess: false,
  isLifetimeMember: false,
  isTrial: false,
};

export const fetchSubscription = async (): Promise<SubscriptionSummary> => {
  try {
    const { data, status } = await subscriptionApi.get<SubscriptionSummary>(
      '/',
      SUBSCRIPTION_READ_CONFIG,
    );
    if (status === 401) {
      return DEFAULT_SUBSCRIPTION;
    }
    return data;
  } catch {
    return DEFAULT_SUBSCRIPTION;
  }
};

export const createCheckoutSession = async (
  interval: BillingInterval,
): Promise<string> => {
  try {
    const { data } = await subscriptionApi.post<{ checkoutUrl: string }>(
      '/checkout',
      { interval },
      SUBSCRIPTION_WRITE_CONFIG,
    );
    if (!data.checkoutUrl) {
      throw new Error('Stripe did not return a checkout URL');
    }
    return data.checkoutUrl;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      throw new Error(SESSION_EXPIRED_MESSAGE);
    }
    throw new Error(
      getApiErrorMessage(error, { action: 'Failed to start checkout' }),
    );
  }
};

export const createPortalSession = async (): Promise<string> => {
  try {
    const { data } = await subscriptionApi.post<{ portalUrl: string }>(
      '/portal',
      {},
      SUBSCRIPTION_WRITE_CONFIG,
    );
    if (!data.portalUrl) {
      throw new Error('Stripe did not return a billing portal URL');
    }
    return data.portalUrl;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      throw new Error(SESSION_EXPIRED_MESSAGE);
    }
    throw new Error(
      getApiErrorMessage(error, { action: 'Failed to open billing portal' }),
    );
  }
};

export type RedeemPromoResult = {
  subscription: SubscriptionSummary;
  bonusDays: number;
  trialEndsAt: string;
};

export const redeemPromoCode = async (code: string): Promise<RedeemPromoResult> => {
  try {
    const { data } = await subscriptionApi.post<RedeemPromoResult>(
      '/redeem-promo',
      { code },
      SUBSCRIPTION_WRITE_CONFIG,
    );
    return data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      throw new Error(SESSION_EXPIRED_MESSAGE);
    }
    throw new Error(
      getApiErrorMessage(error, { action: 'Failed to redeem promo code' }),
    );
  }
};
